import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  Box,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

function InventoryList() {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [expiringAlerts, setExpiringAlerts] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    fetchInventory();
    fetchAlerts();
  }, []);

  useEffect(() => {
    filterInventory();
  }, [searchTerm, inventory]);

  const fetchInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data);
      setFilteredInventory(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setError('Failed to load inventory');
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('token');
      const [lowStockRes, expiringRes] = await Promise.all([
        axios.get(`${API_URL}/inventory/alerts/low-stock`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/inventory/alerts/expiring`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setLowStockAlerts(lowStockRes.data);
      setExpiringAlerts(expiringRes.data);
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  };

  const filterInventory = () => {
    if (!searchTerm.trim()) {
      setFilteredInventory(inventory);
      return;
    }

    const filtered = inventory.filter(item =>
      item.medicine?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.medicine?.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.medicine?.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.location?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredInventory(filtered);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inventory item?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/inventory/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchInventory();
      fetchAlerts();
    } catch (err) {
      console.error('Error deleting inventory:', err);
      setError('Failed to delete inventory item');
    }
  };

  const getStockStatus = (item) => {
    if (item.isLowStock) {
      return <Chip label="Low Stock" color="error" size="small" />;
    }
    if (item.totalStock === 0) {
      return <Chip label="Out of Stock" color="error" size="small" />;
    }
    return <Chip label="In Stock" color="success" size="small" />;
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <Typography>Loading inventory...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon /> Inventory Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/inventory/new')}
        >
          Add Inventory
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Alerts Section */}
      {(lowStockAlerts.length > 0 || expiringAlerts.length > 0) && (
        <Box sx={{ mb: 3 }}>
          {lowStockAlerts.length > 0 && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
              <strong>{lowStockAlerts.length} items</strong> are running low on stock
            </Alert>
          )}
          {expiringAlerts.length > 0 && (
            <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
              <strong>{expiringAlerts.length} batches</strong> are expiring within 30 days
            </Alert>
          )}
        </Box>
      )}

      {/* Search Bar */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by medicine name, category, manufacturer, or location..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
      />

      {/* Inventory Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Medicine Name</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Manufacturer</strong></TableCell>
              <TableCell align="center"><strong>Total Stock</strong></TableCell>
              <TableCell align="center"><strong>Min Level</strong></TableCell>
              <TableCell align="center"><strong>Status</strong></TableCell>
              <TableCell><strong>Location</strong></TableCell>
              <TableCell><strong>Last Restocked</strong></TableCell>
              <TableCell align="center"><strong>Unit Price</strong></TableCell>
              <TableCell align="center"><strong>Units/Pack</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInventory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {searchTerm ? 'No inventory items found matching your search' : 'No inventory items found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredInventory.map((item) => (
                <TableRow
                  key={item.id}
                  sx={{
                    backgroundColor: item.isLowStock ? '#fff3cd' : 'inherit',
                    '&:hover': { backgroundColor: item.isLowStock ? '#ffe69c' : '#f5f5f5' }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {item.medicine?.name}
                      {item.expiringSoonBatches?.length > 0 && (
                        <Tooltip title={`${item.expiringSoonBatches.length} batch(es) expiring soon`}>
                          <WarningIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{item.medicine?.category || '-'}</TableCell>
                  <TableCell>{item.medicine?.manufacturer || '-'}</TableCell>
                  <TableCell align="center">
                    <strong style={{ color: item.totalStock === 0 ? 'red' : 'inherit' }}>
                      {item.totalStock}
                    </strong>
                  </TableCell>
                  <TableCell align="center">{item.minStockLevel || '-'}</TableCell>
                  <TableCell align="center">{getStockStatus(item)}</TableCell>
                  <TableCell>{item.location || '-'}</TableCell>
                  <TableCell>
                    {item.lastRestocked
                      ? new Date(item.lastRestocked).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {item.unitPrice ? (
                      <Box>
                        <span>₹{parseFloat(item.unitPrice).toFixed(2)}/strip</span>
                        {item.unitsPerPackage > 1 && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            ₹{(parseFloat(item.unitPrice) / item.unitsPerPackage).toFixed(2)}/tablet
                          </Typography>
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {item.unitsPerPackage > 1 ? (
                      <Chip label={`${item.unitsPerPackage} tablets`} size="small" color="info" />
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Batches">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => navigate(`/inventory/${item.id}/batches`)}
                      >
                        <InventoryIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => navigate(`/inventory/edit/${item.id}`)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(item.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      <Box sx={{ mt: 3, display: 'flex', gap: 3, justifyContent: 'flex-end' }}>
        <Typography variant="body2">
          <strong>Total Items:</strong> {filteredInventory.length}
        </Typography>
        <Typography variant="body2" color="error">
          <strong>Low Stock Items:</strong> {filteredInventory.filter(i => i.isLowStock).length}
        </Typography>
        <Typography variant="body2" color="error">
          <strong>Out of Stock:</strong> {filteredInventory.filter(i => i.totalStock === 0).length}
        </Typography>
      </Box>
    </Container>
  );
}

export default InventoryList;
